import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from "@/integrations/supabase/client";

interface Setting {
  id: string;
  key: string;
  value: string;
  description: string;
}

interface SettingsContextType {
  settings: Setting[];
  loading: boolean;
  getSetting: (key: string) => string;
  updateSetting: (key: string, value: string) => Promise<boolean>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

interface SettingsProviderProps {
  children: React.ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<Setting[]>([]);
  const [loading, setLoading] = useState(true);
  const subscriptionRef = useRef<any>(null);

  const fetchSettings = async () => {
    try {
      console.log('Fetching settings from database...');
      const { data, error } = await supabase
        .from('settings')
        .select('*')
        .order('key');
      
      if (error) {
        console.error('Error fetching settings:', error);
        return;
      }
      
      console.log('Settings fetched:', data);
      console.log('Cache bust timestamp:', Date.now());
      setSettings(data || []);
    } catch (error) {
      console.error('Unexpected error fetching settings:', error);
    }
  };

  const updateSetting = async (key: string, value: string): Promise<boolean> => {
    try {
      console.log(`Updating setting ${key} to:`, value);
      const { error } = await supabase
        .from('settings')
        .update({ value, updated_at: new Date().toISOString() })
        .eq('key', key);
      
      if (error) {
        console.error('Error updating setting:', error);
        return false;
      }
      
      // Optimistically update local state
      setSettings(prev => prev.map(setting => 
        setting.key === key 
          ? { ...setting, value, updated_at: new Date().toISOString() }
          : setting
      ));
      
      console.log(`Setting ${key} updated successfully`);
      return true;
    } catch (error) {
      console.error('Unexpected error updating setting:', error);
      return false;
    }
  };

  const getSetting = (key: string) => {
    const setting = settings.find(s => s.key === key);
    return setting?.value || '';
  };

  useEffect(() => {
    const loadSettings = async () => {
      setLoading(true);
      await fetchSettings();
      setLoading(false);
    };

    loadSettings();

    // Cleanup existing subscription
    if (subscriptionRef.current) {
      supabase.removeChannel(subscriptionRef.current);
      subscriptionRef.current = null;
    }

    // Create unique channel name
    const channelName = `global-settings-${Date.now()}-${Math.random().toString(36).substring(7)}`;
    
    // Set up single real-time subscription for all settings
    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'settings' 
      }, (payload) => {
        console.log('Settings realtime update:', payload);
        fetchSettings(); // Re-fetch all settings to ensure consistency
      })
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR') {
          console.warn('Settings realtime subscription failed - continuing without realtime updates');
        }
      });

    subscriptionRef.current = channel;

    return () => {
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
        subscriptionRef.current = null;
      }
    };
  }, []);

  const value: SettingsContextType = {
    settings,
    loading,
    getSetting,
    updateSetting
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};